import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, takeUntil } from 'rxjs';
import { SupportService } from '../services/support.service';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { localStorageConstants } from 'src/app/shared/services';

interface ZoneConfig {
  Id: number;
  Name: string;
  HBGFacilityNumber: string;
}

interface SerialOption {
  serialNumber: string;
  raw: any;
}

@Component({
  selector: 'app-qbuilt-support',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './qbuilt-support.component.html',
  styleUrl: './qbuilt-support.component.scss',
})
export class QbuiltSupportComponent implements OnDestroy {
  private destroy$ = new Subject<void>();

  private activeInspectionData: any[] = [];

  // Dropdown data
  plants: string[] = [];
  zones: ZoneConfig[] = [];
  serials: SerialOption[] = [];

  // Selections
  selectedPlant: string = '';
  selectedZone: ZoneConfig | null = null;
  selectedSerial: SerialOption | null = null;

  // State
  loading = false;
  deleting = false;
  noDataForPlant = false;
  noDataForZone = false;

  constructor(
    private supportService: SupportService,
    private dialogService: DialogService,
    private snackBar: MatSnackBar,
  ) {
    this.plants = this.loadPlants();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadPlants(): string[] {
    try {
      const facilities = JSON.parse(localStorage.getItem(localStorageConstants.USER_FACILITIES_KEY) || '[]');
      return facilities
        .map((f: any) => f?.Facility?.KeyChain?.LegacyId as string)
        .filter((id: string) => !!id);
    } catch {
      return [];
    }
  }

  onPlantChange(plant: string): void {
    this.selectedPlant = plant;
    this.selectedZone = null;
    this.selectedSerial = null;
    this.zones = [];
    this.serials = [];
    this.activeInspectionData = [];
    this.noDataForPlant = false;
    this.noDataForZone = false;

    this.loading = true;

    this.supportService
      .getZonesByFacility(plant)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (zones) => {
          this.loading = false;
          this.zones = zones ?? [];
          if (this.zones.length === 0) this.noDataForPlant = true;
        },
        error: () => {
          this.loading = false;
          this.snackBar.open('Failed to load zones.', 'Close', { duration: 4000 });
        },
      });
  }

  onZoneChange(zone: ZoneConfig): void {
    this.selectedZone = zone;
    this.selectedSerial = null;
    this.serials = [];
    this.activeInspectionData = [];
    this.noDataForZone = false;

    const pk = `${this.selectedPlant}-${zone.Id}`;
    this.loading = true;

    this.supportService
      .getInspectionProgress(pk)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (record) => {
          this.loading = false;
          if (!record) {
            this.noDataForZone = true;
            return;
          }
          try {
            this.activeInspectionData = typeof record.InspectionData === 'string'
              ? JSON.parse(record.InspectionData)
              : record.InspectionData ?? [];
          } catch {
            this.activeInspectionData = [];
          }
          this.serials = this.activeInspectionData.map((item) => ({
            serialNumber: item.serialNumber ?? item.SerialNumber ?? '(unknown)',
            raw: item,
          }));
          if (this.serials.length === 0) this.noDataForZone = true;
        },
        error: () => {
          this.loading = false;
          this.snackBar.open('Failed to load inspection data.', 'Close', { duration: 4000 });
        },
      });
  }

  onSerialChange(serial: SerialOption): void {
    this.selectedSerial = serial;
  }

  deleteSerial(): void {
    if (!this.selectedSerial || !this.selectedZone || !this.selectedPlant) return;

    const pk = `${this.selectedPlant}-${this.selectedZone.Id}`;
    const serial = this.selectedSerial.serialNumber;

    const ref = this.dialogService.openDialog(
      'Confirm Delete Inspection',
      `Remove serial #${serial} from zone "${this.selectedZone.Name}" (plant ${this.selectedPlant})? This cannot be undone.`,
      'Cancel',
      'Delete',
      true,
    );

    ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirmed) => {
      if (!confirmed) return;

      this.deleting = true;
      const remaining = this.activeInspectionData.filter(
        (item) => (item.serialNumber ?? item.SerialNumber) !== serial,
      );

      const action$ = remaining.length === 0
        ? this.supportService.deleteInspection(pk)
        : this.supportService.saveInspectionProgress(pk, remaining);

      action$.pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.deleting = false;
          this.snackBar.open(`Serial #${serial} removed successfully.`, 'Close', { duration: 4000 });

          this.activeInspectionData = remaining;
          this.serials = remaining.map((item) => ({
            serialNumber: item.serialNumber ?? item.SerialNumber ?? '(unknown)',
            raw: item,
          }));
          this.selectedSerial = null;

          if (remaining.length === 0) {
            this.noDataForZone = true;
          }
        },
        error: () => {
          this.deleting = false;
          this.snackBar.open('Delete failed. Please try again.', 'Close', { duration: 4000 });
        },
      });
    });
  }
}
